<!-- eslint-disable vue/no-mutating-props -->
<!-- 用户管理、余额、积分与资产流水的列表展示。数据加载和调整弹窗由父页面处理。 -->
<template>
  <section class="user-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
      </article>
    </div>

    <article class="content-card">
      <div class="filter-bar">
        <el-input v-model.trim="filters.keyword" clearable placeholder="昵称/手机号" class="filter-control" @keyup.enter="emit('search')" />
        <div class="filter-row">
          <el-select v-model="filters.level" clearable placeholder="会员等级" class="filter-control">
            <el-option label="普通会员" value="NORMAL" />
            <el-option label="白银会员" value="SILVER" />
            <el-option label="黄金会员" value="GOLD" />
            <el-option label="黑金会员" value="BLACK" />
          </el-select>
          <el-select v-model="filters.adminPermission" clearable placeholder="管理员" class="filter-control">
            <el-option label="管理员" value="ENABLED" />
            <el-option label="非管理员" value="DISABLED" />
          </el-select>
        </div>
        <div class="filter-bar__actions">
          <button class="search-button" @click="emit('search')">搜索</button>
          <button class="reset-button" @click="emit('reset')">重置</button>
        </div>
      </div>

      <div v-if="section === 'management'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>用户</th>
              <th>手机号</th>
              <th>会员等级</th>
              <th class="is-right">订单数</th>
              <th class="is-right">可用余额</th>
              <th class="is-right">累计消费</th>
              <th>最近消费</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in users" :key="item.id">
              <td><span class="cell-text">{{ item.nickname || item.id }}</span></td>
              <td><span class="cell-text">{{ item.phone || '--' }}</span></td>
              <td><span :class="['status-tag', toneClass(userLevelTone(item.level))]">{{ getUserLevelText(item.levelName, item.level) }}</span></td>
              <td class="is-right">{{ item.orders ?? 0 }}</td>
              <td class="is-right">{{ formatAmount(item.balance ?? 0) }}</td>
              <td class="is-right">{{ formatAmount(item.amount ?? 0) }}</td>
              <td>{{ item.last || '--' }}</td>
              <td><span :class="['status-tag', toneClass(userStatusTone(item.status))]">{{ getUserStatusText(item.status) }}</span></td>
              <td class="is-right">
                <div class="row-actions">
                  <button class="row-actions__text" @click="emit('manageUser', item)">管理</button>
                  <button class="row-actions__text" @click="emit('viewOrders', item)">订单</button>
                  <button class="row-actions__text is-danger" @click="emit('toggleStatus', item)">{{ isFrozenUser(item) ? '解封' : '封禁' }}</button>
                  <button class="row-actions__text is-danger" @click="emit('deleteUser', item)">删除</button>
                </div>
              </td>
            </tr>
            <tr v-if="!users.length && !loadingUsers"><td colspan="9"><el-empty description="暂无用户数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'balance'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>用户</th>
              <th>手机号</th>
              <th class="is-right">可用余额</th>
              <th>会员等级</th>
              <th>更新时间</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in users" :key="item.id">
              <td><span class="cell-text">{{ item.nickname || item.id }}</span></td>
              <td><span class="cell-text">{{ item.phone || '--' }}</span></td>
              <td class="is-right">{{ formatAmount(item.balance ?? 0) }}</td>
              <td><span class="cell-text">{{ getUserLevelText(item.levelName, item.level) }}</span></td>
              <td>{{ item.updatedAt || '--' }}</td>
              <td class="is-right"><div class="row-actions"><button title="余额调整" @click="emit('adjustBalance', item)"><el-icon><EditPen /></el-icon></button></div></td>
            </tr>
            <tr v-if="!users.length && !loadingUsers"><td colspan="6"><el-empty description="暂无余额数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="section === 'points'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>用户</th>
              <th>手机号</th>
              <th class="is-right">可用积分</th>
              <th class="is-right">成长值</th>
              <th>会员等级</th>
              <th>更新时间</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in users" :key="item.id">
              <td><span class="cell-text">{{ item.nickname || item.id }}</span></td>
              <td><span class="cell-text">{{ item.phone || '--' }}</span></td>
              <td class="is-right">{{ item.points ?? 0 }}</td>
              <td class="is-right">{{ item.growth ?? 0 }}</td>
              <td><span class="cell-text">{{ getUserLevelText(item.levelName, item.level) }}</span></td>
              <td>{{ item.updatedAt || '--' }}</td>
              <td class="is-right"><div class="row-actions"><button title="积分调整" @click="emit('adjustPoints', item)"><el-icon><EditPen /></el-icon></button></div></td>
            </tr>
            <tr v-if="!users.length && !loadingUsers"><td colspan="7"><el-empty description="暂无积分数据" /></td></tr>
          </tbody>
        </table>
      </div>

      <div v-else class="analysis-grid">
        <section class="analysis-block">
          <h3>余额流水</h3>
          <table>
            <thead>
              <tr>
                <th>用户</th>
                <th>类型</th>
                <th class="is-right">变动金额</th>
                <th class="is-right">变动后余额</th>
                <th>时间</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in walletPage?.list ?? []" :key="`wallet-${item.id}`">
                <td><span class="cell-text">{{ item.nickname || item.userId }}</span></td>
                <td><span class="cell-text">{{ getWalletLogTypeText(item.type) }}</span></td>
                <td class="is-right">{{ formatSignedAmount(item.changeAmount) }}</td>
                <td class="is-right">{{ formatAmount(item.balanceAfter ?? 0) }}</td>
                <td>{{ item.createdAt || '--' }}</td>
              </tr>
              <tr v-if="!(walletPage?.list.length) && !loadingWalletLogs"><td colspan="5"><el-empty description="暂无余额流水" /></td></tr>
            </tbody>
          </table>
        </section>
        <section class="analysis-block">
          <h3>积分流水</h3>
          <table>
            <thead>
              <tr>
                <th>用户</th>
                <th>类型</th>
                <th class="is-right">变动积分</th>
                <th class="is-right">变动后积分</th>
                <th>时间</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in pointsPage?.list ?? []" :key="`points-${item.id}`">
                <td><span class="cell-text">{{ item.nickname || item.userId }}</span></td>
                <td><span class="cell-text">{{ getPointsLogTypeText(item.type) }}</span></td>
                <td class="is-right">{{ formatSignedNumber(item.changePoints) }}</td>
                <td class="is-right">{{ item.pointsAfter ?? 0 }}</td>
                <td>{{ item.createdAt || '--' }}</td>
              </tr>
              <tr v-if="!(pointsPage?.list.length) && !loadingPointsLogs"><td colspan="5"><el-empty description="暂无积分流水" /></td></tr>
            </tbody>
          </table>
        </section>
      </div>

      <div v-if="section !== 'assets' && userPage" class="table-footer">
        <span>共 {{ userPage.total }} 条记录，第 {{ userPage.page }}/{{ userTotalPages }} 页</span>
        <div>
          <button :disabled="userPage.page <= 1" @click="emit('changeUserPage', userPage.page - 1)">上一页</button>
          <button class="is-active">{{ userPage.page }}</button>
          <button :disabled="!userPage.hasMore" @click="emit('changeUserPage', userPage.page + 1)">下一页</button>
        </div>
      </div>
      <div v-else-if="section === 'assets' && (walletPage || pointsPage)" class="table-footer">
        <span>余额流水共 {{ walletPage?.total ?? 0 }} 条，积分流水共 {{ pointsPage?.total ?? 0 }} 条</span>
        <div>
          <button :disabled="(walletPage?.page ?? 1) <= 1 || (pointsPage?.page ?? 1) <= 1" @click="emit('changeAssetPage', Math.min((walletPage?.page ?? 1), (pointsPage?.page ?? 1)) - 1)">上一页</button>
          <button class="is-active">{{ Math.max(walletPage?.page ?? 1, pointsPage?.page ?? 1) }}</button>
          <button :disabled="!(walletPage?.hasMore || pointsPage?.hasMore)" @click="emit('changeAssetPage', Math.max(walletPage?.page ?? 1, pointsPage?.page ?? 1) + 1)">下一页</button>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { toRefs } from 'vue'
import { EditPen } from '@element-plus/icons-vue'
import type { PageResult } from '@jiaofa/api-types'
import type { AdminConsumerUser, AdminPointsLog, AdminUsersParams, AdminWalletLog } from '@/api/user'
import type { OperationStat } from '@/data/operationDesign'

type UserSection = 'management' | 'balance' | 'points' | 'assets'
type StatusTone = 'brand' | 'success' | 'warning' | 'error' | 'muted'

const props = defineProps<{
  section: UserSection
  stats: OperationStat[]
  filters: AdminUsersParams
  users: AdminConsumerUser[]
  userPage?: PageResult<AdminConsumerUser>
  walletPage?: PageResult<AdminWalletLog>
  pointsPage?: PageResult<AdminPointsLog>
  loadingUsers: boolean
  loadingWalletLogs: boolean
  loadingPointsLogs: boolean
  userTotalPages: number
  formatAmount: (amountInFen: number) => string
  formatSignedAmount: (amountInFen?: number) => string
  formatSignedNumber: (value?: number) => string
  getUserLevelText: (levelName?: string, level?: string) => string
  getUserStatusText: (status?: string) => string
  getWalletLogTypeText: (type?: string) => string
  getPointsLogTypeText: (type?: string) => string
  userLevelTone: (level?: string) => StatusTone
  userStatusTone: (status?: string) => StatusTone
  toneClass: (tone: StatusTone) => string
}>()

const emit = defineEmits<{
  search: []
  reset: []
  manageUser: [user: AdminConsumerUser]
  viewOrders: [user: AdminConsumerUser]
  toggleStatus: [user: AdminConsumerUser]
  deleteUser: [user: AdminConsumerUser]
  adjustBalance: [user: AdminConsumerUser]
  adjustPoints: [user: AdminConsumerUser]
  changeUserPage: [page: number]
  changeAssetPage: [page: number]
}>()

const {
  section,
  stats,
  filters,
  users,
  userPage,
  walletPage,
  pointsPage,
  loadingUsers,
  loadingWalletLogs,
  loadingPointsLogs,
  userTotalPages,
  formatAmount,
  formatSignedAmount,
  formatSignedNumber,
  getUserLevelText,
  getUserStatusText,
  getWalletLogTypeText,
  getPointsLogTypeText,
  userLevelTone,
  userStatusTone,
  toneClass,
} = toRefs(props)

function isFrozenUser(user: AdminConsumerUser): boolean {
  return user.status === 'FROZEN' || user.status === 'DISABLED'
}
</script>

<style scoped lang="scss">
.user-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-control { width: 220px; }
.filter-row { display: flex; gap: 12px; }
.filter-row .filter-control { min-width: 124px; }
.filter-bar__actions { display: flex; gap: 8px; margin-left: auto; }
button { border: 0; cursor: pointer; font: inherit; }
.search-button, .reset-button { height: 36px; padding: 0 16px; border-radius: 8px; font-size: 14px; font-weight: 700; }
.search-button { background: var(--jf-color-primary); color: #fff; }
.reset-button { border: 1px solid var(--jf-color-border); background: #fff; color: var(--jf-color-text-primary); }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.is-right { text-align: right; }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.row-actions { display: inline-flex; justify-content: flex-end; gap: 14px; color: var(--jf-color-primary-dark); }
.row-actions button { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; background: transparent; color: inherit; }
.row-actions .row-actions__text { width: auto; height: 28px; padding: 0; border-radius: 0; display: inline-flex; align-items: center; background: transparent; color: var(--jf-color-primary-dark); font-weight: 700; }
.row-actions .row-actions__text.is-danger { color: var(--jf-color-error); }
.row-actions button:hover { background: rgba(179, 218, 247, .3); }
.row-actions .row-actions__text:hover { background: transparent; text-decoration: underline; text-underline-offset: 3px; }
.row-actions button:disabled, .table-footer button:disabled { cursor: not-allowed; opacity: .5; }
.analysis-grid { padding: 16px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; }
.analysis-block { min-width: 0; }
.analysis-block h3 { margin: 0 0 12px; color: var(--jf-color-text-primary); font-size: 16px; }
.analysis-block table { border: 1px solid var(--jf-color-border); border-radius: 8px; overflow: hidden; }
.table-footer { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; color: var(--jf-color-text-secondary); font-size: 14px; }
.table-footer div { display: flex; gap: 6px; }
.table-footer button { min-width: 32px; height: 32px; padding: 0 10px; border: 1px solid var(--jf-color-border); border-radius: 8px; background: #fff; color: var(--jf-color-text-secondary); }
.table-footer .is-active { border-color: var(--jf-color-primary); background: var(--jf-color-primary); color: #fff; }
@media (max-width: 1180px) {
  .summary-grid, .analysis-grid { grid-template-columns: 1fr; }
}
</style>
